import { Plus } from 'lucide-react';
import { type CSSProperties, type ReactElement, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { QuickCreateSheet } from '@/components/reminders/quick-create-sheet';
import { useFeedback } from '@/hooks/use-feedback';
import { useLongPress } from '@/hooks/use-long-press';
import { isReminderWizardFullscreenPath } from '@/hooks/use-reminder-wizard-routes';
import { useI18n } from '@/i18n';
import { QUICK_CREATE_PRESETS } from '@/lib/quick-create-reminder';
import { cn } from '@/lib/utils';
import { useCreateReminderWizard } from '@/stores/create-reminder-wizard';

export function CreateReminderFab(): ReactElement {
  const { t } = useI18n();
  const { tap } = useFeedback();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const beginCreate = useCreateReminderWizard((s) => s.beginCreate);
  const [sheetOpen, setSheetOpen] = useState(false);

  const isInWizard = isReminderWizardFullscreenPath(location.pathname);
  const isEdit = location.pathname.includes('/edit');

  const openCreate = (): void => {
    beginCreate();
    void navigate('/reminders/new');
  };

  const exitWizard = (): void => {
    if (isEdit && id) {
      void navigate(`/reminders/${id}`);
      return;
    }
    void navigate('/reminders');
  };

  const { isPressing, progress, handlers } = useLongPress({
    onClick: () => {
      tap();
      if (isInWizard) {
        exitWizard();
        return;
      }
      openCreate();
    },
    onLongPress: () => {
      if (isInWizard) {
        return;
      }
      setSheetOpen(true);
    },
  });

  const handlePresetSelect = (presetId: string): void => {
    const preset = QUICK_CREATE_PRESETS.find((item) => item.id === presetId);
    setSheetOpen(false);
    beginCreate(preset?.config);
    void navigate('/reminders/new');
  };

  return (
    <>
      <button
        type="button"
        style={isPressing ? ({ '--long-press-progress': progress } as CSSProperties) : undefined}
        className={cn(
          'studio-create-trigger fab-long-press pressable relative flex h-[56px] w-[56px] items-center justify-center rounded-full',
          isInWizard && 'studio-create-trigger-close',
          isPressing && 'fab-long-press-holding shadow-md',
        )}
        aria-label={isInWizard ? t('common.close') : t('nav.createReminder')}
        aria-haspopup={isInWizard ? undefined : 'dialog'}
        {...handlers}
      >
        <Plus
          className={cn('studio-create-icon h-6 w-6', isInWizard && 'studio-create-icon-close')}
          strokeWidth={2}
          aria-hidden
        />
      </button>

      <QuickCreateSheet
        open={sheetOpen}
        onClose={() => {
          setSheetOpen(false);
        }}
        onSelect={handlePresetSelect}
      />
    </>
  );
}
